import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Calendar, Save, FileText, Upload, Trash2, Download, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const initialGradeData = {
  morning: { boys: 0, girls: 0, total: 0 },
  afternoon: { boys: 0, girls: 0, total: 0 }
};

export default function DailyAttendance() {
  const { user } = useAuth();
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);
  const [formData, setFormData] = useState({
    grade_7: JSON.parse(JSON.stringify(initialGradeData)),
    grade_8: JSON.parse(JSON.stringify(initialGradeData)),
    grade_9: JSON.parse(JSON.stringify(initialGradeData)),
    grade_10: JSON.parse(JSON.stringify(initialGradeData)),
    grade_11: JSON.parse(JSON.stringify(initialGradeData)),
    teachers: { total_teachers: 0, present: 0, absent: 0 }
  });
  const [absentReturnsFiles, setAbsentReturnsFiles] = useState([]);
  const [statsFiles, setStatsFiles] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingExcel, setUploadingExcel] = useState(false);

  useEffect(() => {
    fetchReports();
    checkExistingReport();
    fetchDailyFiles();
  }, [date]);

  const fetchDailyFiles = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/daily-report-files?report_date=${date}`,
        { withCredentials: true }
      );
      setAbsentReturnsFiles(data.filter(f => f.file_category === 'absent_returns'));
      setStatsFiles(data.filter(f => f.file_category === 'stats'));
    } catch (error) {
      console.error('Error fetching files:', error);
    }
  };

  const checkExistingReport = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/attendance/daily-reports/${date}`,
        { withCredentials: true }
      );
      if (data) {
        setFormData({
          grade_7: data.grade_7,
          grade_8: data.grade_8,
          grade_9: data.grade_9,
          grade_10: data.grade_10,
          grade_11: data.grade_11,
          teachers: data.teachers
        });
      }
    } catch (error) {
      // No existing report, keep empty form
    }
  };

  const fetchReports = async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/attendance/daily-reports?limit=10`,
        { withCredentials: true }
      );
      setReports(data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    }
  };

  const updateGradeSession = (grade, session, field, value) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => {
      const newData = { ...prev };
      newData[grade][session][field] = numValue;
      
      // Auto-calculate total
      if (field === 'boys' || field === 'girls') {
        newData[grade][session].total = 
          newData[grade][session].boys + newData[grade][session].girls;
      }
      
      return newData;
    });
  };

  const updateTeachers = (field, value) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      teachers: { ...prev.teachers, [field]: numValue }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        date,
        ...formData
      };

      // Check if updating existing report
      try {
        await axios.get(
          `${BACKEND_URL}/api/attendance/daily-reports/${date}`,
          { withCredentials: true }
        );
        // Report exists, update it
        await axios.put(
          `${BACKEND_URL}/api/attendance/daily-report/${date}`,
          payload,
          { withCredentials: true }
        );
        toast.success('Daily report updated successfully!');
      } catch {
        // Report doesn't exist, create new
        await axios.post(
          `${BACKEND_URL}/api/attendance/daily-report`,
          payload,
          { withCredentials: true }
        );
        toast.success('Daily report submitted successfully!');
      }

      fetchReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit report');
    } finally {
      setLoading(false);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx or .xls)');
      return;
    }

    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(
        `${BACKEND_URL}/api/attendance/upload-excel`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success(data.message);
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach(err => toast.error(err));
      }
      fetchReports();
      checkExistingReport();
      e.target.value = ''; // Reset input
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload Excel file');
    } finally {
      setUploadingExcel(false);
    }
  };

  const handleFileUpload = async (e, category) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingFile(true);
    try {
      const fileFormData = new FormData();
      fileFormData.append('report_date', date);
      fileFormData.append('file_category', category);
      fileFormData.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/daily-report-files`,
        fileFormData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success(`${category === 'absent_returns' ? 'Absent Returns' : 'Stats'} file uploaded successfully`);
      fetchDailyFiles();
      e.target.value = ''; // Reset input
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleDeleteFile = async (fileId, category) => {
    if (!window.confirm('Are you sure you want to delete this file?')) return;

    try {
      await axios.delete(
        `${BACKEND_URL}/api/daily-report-files/${fileId}`,
        { withCredentials: true }
      );
      toast.success('File deleted successfully');
      fetchDailyFiles();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const GradeSection = ({ grade, gradeNumber }) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold text-[#0F172A]">Grade {gradeNumber}</h3>
      
      {/* Morning */}
      <div>
        <h4 className="text-sm font-medium text-[#64748B] mb-3">Morning Session</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Boys</Label>
            <Input
              type="number"
              min="0"
              data-testid={`grade-${gradeNumber}-morning-boys`}
              value={formData[grade].morning.boys}
              onChange={(e) => updateGradeSession(grade, 'morning', 'boys', e.target.value)}
            />
          </div>
          <div>
            <Label>Girls</Label>
            <Input
              type="number"
              min="0"
              data-testid={`grade-${gradeNumber}-morning-girls`}
              value={formData[grade].morning.girls}
              onChange={(e) => updateGradeSession(grade, 'morning', 'girls', e.target.value)}
            />
          </div>
          <div>
            <Label>Total</Label>
            <Input
              type="number"
              value={formData[grade].morning.total}
              readOnly
              className="bg-[#F9FAFB]"
              data-testid={`grade-${gradeNumber}-morning-total`}
            />
          </div>
        </div>
      </div>

      {/* Afternoon */}
      <div>
        <h4 className="text-sm font-medium text-[#64748B] mb-3">Afternoon Session</h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label>Boys</Label>
            <Input
              type="number"
              min="0"
              data-testid={`grade-${gradeNumber}-afternoon-boys`}
              value={formData[grade].afternoon.boys}
              onChange={(e) => updateGradeSession(grade, 'afternoon', 'boys', e.target.value)}
            />
          </div>
          <div>
            <Label>Girls</Label>
            <Input
              type="number"
              min="0"
              data-testid={`grade-${gradeNumber}-afternoon-girls`}
              value={formData[grade].afternoon.girls}
              onChange={(e) => updateGradeSession(grade, 'afternoon', 'girls', e.target.value)}
            />
          </div>
          <div>
            <Label>Total</Label>
            <Input
              type="number"
              value={formData[grade].afternoon.total}
              readOnly
              className="bg-[#F9FAFB]"
              data-testid={`grade-${gradeNumber}-afternoon-total`}
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div data-testid="daily-attendance-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          Daily Attendance Report
        </h1>
        <p className="text-[#64748B] mt-2">Submit daily attendance for all grades and teachers</p>
      </div>

      {/* Excel Upload Card */}
      <Card className="mb-6 shadow-lg border-none bg-gradient-to-br from-[#667EEA]/5 to-[#764BA2]/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#667EEA]" />
            Quick Upload from Excel
          </CardTitle>
          <CardDescription>
            Upload an Excel file to automatically populate attendance reports. File should include columns: Date, Grade, Session, Gender, Present
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <input
              type="file"
              id="excel-upload"
              className="hidden"
              accept=".xlsx,.xls"
              onChange={handleExcelUpload}
              disabled={uploadingExcel}
            />
            <Button
              onClick={() => document.getElementById('excel-upload').click()}
              disabled={uploadingExcel}
              className="flex-1 bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
              data-testid="upload-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {uploadingExcel ? 'Uploading...' : 'Upload Excel File'}
            </Button>
            <a
              href={`${BACKEND_URL}/api/attendance/excel-template`}
              download="Daily_Attendance_Template.xlsx"
            >
              <Button
                variant="outline"
                className="border-[#667EEA] text-[#667EEA] hover:bg-[#667EEA]/10"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                Download Template
              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="input" className="space-y-6">
        <TabsList>
          <TabsTrigger value="input" data-testid="tab-input">
            <Calendar className="w-4 h-4 mr-2" />
            Input Report
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <FileText className="w-4 h-4 mr-2" />
            Report History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="input">
          <form onSubmit={handleSubmit} className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Select Date</CardTitle>
                    <CardDescription>Choose the date for this attendance report</CardDescription>
                  </div>
                  <Input
                    type="date"
                    data-testid="report-date-input"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-48"
                  />
                </div>
              </CardHeader>
            </Card>

            {/* Students Section */}
            <Card>
              <CardHeader>
                <CardTitle>Student Attendance</CardTitle>
                <CardDescription>Enter attendance for each grade (morning and afternoon)</CardDescription>
              </CardHeader>
              <CardContent className="space-y-8">
                <GradeSection grade="grade_7" gradeNumber="7" />
                <div className="border-t border-[#E2E8F0] my-6"></div>
                <GradeSection grade="grade_8" gradeNumber="8" />
                <div className="border-t border-[#E2E8F0] my-6"></div>
                <GradeSection grade="grade_9" gradeNumber="9" />
                <div className="border-t border-[#E2E8F0] my-6"></div>
                <GradeSection grade="grade_10" gradeNumber="10" />
                <div className="border-t border-[#E2E8F0] my-6"></div>
                <GradeSection grade="grade_11" gradeNumber="11" />
              </CardContent>
            </Card>

            {/* Teachers Section */}
            <Card>
              <CardHeader>
                <CardTitle>Teacher Attendance</CardTitle>
                <CardDescription>Enter daily teacher attendance statistics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <Label>Total Number of Teachers</Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid="teachers-total-input"
                      value={formData.teachers.total_teachers}
                      onChange={(e) => updateTeachers('total_teachers', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Total Present</Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid="teachers-present-input"
                      value={formData.teachers.present}
                      onChange={(e) => updateTeachers('present', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Total Absent</Label>
                    <Input
                      type="number"
                      min="0"
                      data-testid="teachers-absent-input"
                      value={formData.teachers.absent}
                      onChange={(e) => updateTeachers('absent', e.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              type="submit"
              data-testid="submit-daily-report-btn"
              className="w-full bg-[#0F172A] hover:bg-[#1E293B]"
              disabled={loading}
            >
              <Save className="w-4 h-4 mr-2" />
              {loading ? 'Submitting...' : 'Submit Daily Report'}
            </Button>
          </form>
        </TabsContent>

        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>Recent Reports</CardTitle>
              <CardDescription>View previously submitted daily reports</CardDescription>
            </CardHeader>
            <CardContent>
              {reports.length === 0 ? (
                <div className="text-center py-12 text-[#64748B]">
                  No reports found
                </div>
              ) : (
                <div className="border rounded-md">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Grades 7-11 Total</TableHead>
                        <TableHead>Teachers Present</TableHead>
                        <TableHead>Submitted By</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reports.map((report) => {
                        const totalStudents = 
                          report.grade_7.morning.total + report.grade_7.afternoon.total +
                          report.grade_8.morning.total + report.grade_8.afternoon.total +
                          report.grade_9.morning.total + report.grade_9.afternoon.total +
                          report.grade_10.morning.total + report.grade_10.afternoon.total +
                          report.grade_11.morning.total + report.grade_11.afternoon.total;
                        
                        return (
                          <TableRow key={report.id} data-testid="report-row">
                            <TableCell className="font-medium">{report.date}</TableCell>
                            <TableCell>{totalStudents}</TableCell>
                            <TableCell>{report.teachers.present} / {report.teachers.total_teachers}</TableCell>
                            <TableCell className="text-[#64748B]">{report.submitted_by}</TableCell>
                            <TableCell>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setDate(report.date);
                                  setFormData({
                                    grade_7: report.grade_7,
                                    grade_8: report.grade_8,
                                    grade_9: report.grade_9,
                                    grade_10: report.grade_10,
                                    grade_11: report.grade_11,
                                    teachers: report.teachers
                                  });
                                  document.querySelector('[data-testid="tab-input"]').click();
                                }}
                                data-testid="edit-report-btn"
                              >
                                Edit
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* File Upload Portals */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Absent Returns Portal */}
        <Card className="shadow-lg border-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#667EEA]" />
              Absent Returns
            </CardTitle>
            <CardDescription>Upload absent return documents for {date}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <input
                type="file"
                id="absent-returns-upload"
                className="hidden"
                accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, 'absent_returns')}
                disabled={uploadingFile}
              />
              <Button
                onClick={() => document.getElementById('absent-returns-upload').click()}
                disabled={uploadingFile}
                className="w-full bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
                data-testid="upload-absent-returns-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingFile ? 'Uploading...' : 'Upload File'}
              </Button>

              {absentReturnsFiles.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Uploaded Files</Label>
                  {absentReturnsFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-[#F9FAFB] border border-[#E2E8F0] rounded-md"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <FileText className="w-4 h-4 text-[#667EEA]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.original_filename}</p>
                          <p className="text-xs text-[#718096]">
                            {new Date(file.uploaded_at).toLocaleDateString()} by {file.uploaded_by}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`${BACKEND_URL}/api/daily-report-files/${file.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        {(user.role === 'admin' || user.role === 'board_member') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFile(file.id, 'absent_returns')}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Stats Portal */}
        <Card className="shadow-lg border-none">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#667EEA]" />
              Stats
            </CardTitle>
            <CardDescription>Upload statistics documents for {date}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <input
                type="file"
                id="stats-upload"
                className="hidden"
                accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.jpg,.jpeg,.png"
                onChange={(e) => handleFileUpload(e, 'stats')}
                disabled={uploadingFile}
              />
              <Button
                onClick={() => document.getElementById('stats-upload').click()}
                disabled={uploadingFile}
                className="w-full bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
                data-testid="upload-stats-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingFile ? 'Uploading...' : 'Upload File'}
              </Button>

              {statsFiles.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Uploaded Files</Label>
                  {statsFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-[#F9FAFB] border border-[#E2E8F0] rounded-md"
                    >
                      <div className="flex items-center gap-2 flex-1">
                        <FileText className="w-4 h-4 text-[#667EEA]" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{file.original_filename}</p>
                          <p className="text-xs text-[#718096]">
                            {new Date(file.uploaded_at).toLocaleDateString()} by {file.uploaded_by}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <a
                          href={`${BACKEND_URL}/api/daily-report-files/${file.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Button size="sm" variant="outline">
                            <Download className="w-4 h-4" />
                          </Button>
                        </a>
                        {(user.role === 'admin' || user.role === 'board_member') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDeleteFile(file.id, 'stats')}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
